/* FSS grain-protectant estimator plugin for the FSS collection components (V3, 2026-09-27; V6: rateModel + cost at a chosen rate).
   One comparison value per product and commodity, used by the comparison table and the "Help me choose" cards.
   Data (all from snippets/fss-gp-rates.liquid — the SAME file the Grain Protectant Calculator reads):
     FSS_GP_RATES[calcKey][name]    = { r: rate | [low, mid, high], b: "bu" (per 1,000 bu) | "ton" | "30ton", d: carrier }
     FSS_GP_TW[name]                = standard lb per bushel (used ONLY to convert a per-ton label rate to bushels)
     FSS_GP_PACKS[variantId]        = { n: net product in the package, u: "fl oz" | "lb" }   (never shipping weight)
     FSS_GP_VERIFIED[calcKey][name] = { p: [label-printed points], c: comparison rate, cr: rule, cn: note, note, src }
       (cnh = the note for the higher of two separate printed levels, when cn describes the lower one)
       cr = "printed_middle" | "range_midpoint" | "fixed" | "derived" | "lower_printed". A combination without c has no
       comparison value (em dash).
     FSS_GP_FORMDEP[name]           = commodity whose bushel conversion depends on its form (rice).
   Package basis = the product's configured bulk variant (basisVariant) when it is offered and priced; otherwise the
   largest offered package by net content, labelled as a fallback when a configured basis was unavailable.
   Estimated chemical cost = package price ÷ net content × comparison rate ÷ rate divisor (× conversion). No
   intermediate rounding; the display rounds once (¢/bu to 2 decimals; feed in $/US short ton to 2 decimals). */
(function () {
  'use strict';
  var R, TW, PK, V, FD, PL = {};
  function load() { R = window.FSS_GP_RATES || {}; TW = window.FSS_GP_TW || {}; PK = window.FSS_GP_PACKS || {}; V = window.FSS_GP_VERIFIED || {}; FD = window.FSS_GP_FORMDEP || {}; }
  // Section config: pkgLabels = ["variantId=Display name", ...] (one curated name per comparison package)
  function init(cfg) {
    load();
    (cfg && cfg.pkgLabels || []).forEach(function (line) { var i = String(line).indexOf('='); if (i > 0) PL[String(line).slice(0, i).trim()] = String(line).slice(i + 1).trim(); });
  }
  function num(n, dp) { return Number(n).toLocaleString('en-US', { maximumFractionDigits: dp == null ? 4 : dp }); }
  function usd(d) { return '$' + d.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  function per(b) { return b === 'bu' ? 'per 1,000 bu' : b === '30ton' ? 'per 30 tons' : 'per ton'; }
  function divisor(b) { return b === 'bu' ? 1000 : b === '30ton' ? 30 : 1; }
  var RULE_TEXT = { printed_middle: 'the label’s printed middle rate', range_midpoint: 'midpoint of the label range — a comparison benchmark, not a label recommendation', fixed: 'the label’s single applicable rate', derived: 'the label’s single protectant rate, converted to concentrate', lower_printed: 'the lower of the label’s two separate printed levels (not an average)' };

  // Which dataset entry applies to this product and choice (first matching name wins).
  function entry(p, c) {
    var key = p.calcKey;
    if (!key || !R[key]) return null;
    var names = (c.rateNames || []).filter(Boolean);
    for (var i = 0; i < names.length; i++) {
      var n = names[i].trim();
      if (R[key][n]) return { key: key, name: n, rate: R[key][n], ver: (V[key] || {})[n] || null };
    }
    return null;
  }
  function netOf(v) { var pk = v && PK[String(v.id)]; return pk && pk.n > 0 ? pk : null; }
  function basisPackage(p) {
    var offered = p.variants.filter(function (v) { return v.available && v.price > 0 && netOf(v); });
    var largest = offered.slice().sort(function (a, b) { return (netOf(b).n - netOf(a).n) || (a.price - b.price); })[0] || null;
    if (p.basisVariant) {
      var want = p.variants.filter(function (v) { return String(v.id) === String(p.basisVariant); })[0];
      if (want && want.available && want.price > 0 && netOf(want)) return { v: want, fallback: false };
      return largest ? { v: largest, fallback: true, intended: want ? want.title : 'configured package' } : null;
    }
    return largest ? { v: largest, fallback: false } : null;
  }
  function pkgName(v) { var pk = netOf(v); if (PL[String(v.id)]) return PL[String(v.id)]; return (!v.title || v.title === 'Default Title') ? num(pk.n) + ' ' + pk.u + ' package' : v.title; }
  function rateNum(c) { return num(c, c < 1 ? 4 : 2); } // display only; the maths keeps the full value

  /* V6 rate choices for one product and commodity, from the SAME records the calculator uses.
     FSS_GP_VERIFIED may add: o = explicit option amounts (when the calculator's own r is not the label ladder),
     om = "lmh" | "lh" | "single" | "see" (see = no comparable single use; show the condition, no number),
     od = default option index, on = short condition shown with the rate.
     Without them: lower_printed → the two printed levels (Low/High, lower default); a three-point r → Low/Medium/High
     (default = the verified comparison rate c); otherwise the single applicable rate. A Medium that is not a printed
     label point is flagged (printed: false) so the page can say it is an arithmetic midpoint. */
  var LV3 = ['Low', 'Medium', 'High'], LV2 = ['Low', 'High'];
  function near(a, b) { return Math.abs(a - b) < 1e-6 * Math.max(1, Math.abs(a), Math.abs(b)); }
  function rateModel(p, c) {
    load();
    if ((p.direct || []).indexOf(c.key) < 0) return { status: 'na' };
    var e = entry(p, c);
    if (!e || !e.ver) return { status: 'unverified' };
    var ver = e.ver, r = e.rate.r, b = e.rate.b, pts = (Array.isArray(ver.o) && Array.isArray(ver.op)) ? ver.op : (ver.p || []), vals, mode;
    var unit = p.form === 'dry' ? 'lb' : 'fl oz';
    var base = { key: e.key, name: e.name, basis: b, unit: unit, cond: ver.on || '', note: ver.cn || '', feed: c.basis === 'ton', cr: ver.cr || '' };
    if (ver.om === 'see') { base.status = 'see'; return base; }
    if (Array.isArray(ver.o) && ver.o.length) { vals = ver.o.slice(); mode = ver.om || (vals.length === 3 ? 'lmh' : vals.length === 2 ? 'lh' : 'single'); }
    else if (ver.cr === 'lower_printed' && pts.length === 2) { vals = pts.slice(); mode = 'lh'; }
    else if (Array.isArray(r) && r.length === 3) { vals = r.slice(); mode = 'lmh'; }
    else { vals = [ver.c > 0 && ver.cr !== 'range_midpoint' ? ver.c : (Array.isArray(r) ? r[0] : r)]; mode = 'single'; }
    var names = mode === 'lmh' ? LV3 : mode === 'lh' ? LV2 : ['Single'];
    var opts = vals.map(function (v, i) {
      var printed = pts.length ? pts.some(function (x) { return near(x, v); }) : (mode === 'single');
      return { t: names[i] || ('Level ' + (i + 1)), v: v, printed: printed };
    });
    var def = typeof ver.od === 'number' ? ver.od : 0;
    if (typeof ver.od !== 'number') {
      if (mode === 'lmh') { def = 1; if (ver.c > 0) vals.forEach(function (v, i) { if (near(v, ver.c)) def = i; }); }
      else def = 0;
    }
    base.status = 'ok'; base.mode = mode; base.options = opts; base.def = Math.min(Math.max(def, 0), opts.length - 1);
    return base;
  }
  function unitText(m) { var nb = ' '; return m.basis === 'bu' ? 'per' + nb + '1,000' + nb + 'bu' : m.basis === '30ton' ? 'per 30' + nb + 'tons' : m.feed ? 'per US' + nb + 'ton of feed' : 'per ton of grain'; }

  /* status: ok | na (no direct use) | unresolved (no single comparison rate) | riceForm | noPrice
     value: dollars per bushel (unit "bu") or per US short ton (unit "ton").
     V6: an optional rate (same units as the record) replaces the comparison rate c, e.g. the row's chosen level. */
  function comparison(p, c, rateOverride) {
    load();
    if ((p.direct || []).indexOf(c.key) < 0) return { status: 'na', reason: 'Not included in this comparison for ' + c.label.toLowerCase() + '; check the product label' };
    if (c.basis === 'none') return { status: 'na', reason: 'No cost estimate for ' + c.label.toLowerCase() };
    var e = entry(p, c);
    // rice form is decided first, so a per-ton rice row explains itself
    if (e && c.basis !== 'ton' && (e.rate.b === 'ton' || e.rate.b === '30ton') && FD[e.name]) return { status: 'riceForm', short: 'Varies by rice form', reason: 'Per-ton label rate; the bushel weight depends on the ' + c.label.toLowerCase() + ' form' };
    var useRate = rateOverride > 0 ? rateOverride : (e && e.ver ? e.ver.c : 0);
    if (!e || !e.ver || !(useRate > 0)) return { status: 'unresolved', short: (e && e.ver && e.ver.us) || 'No single rate', reason: (e && e.ver && e.ver.u) || ('No single comparison rate for ' + c.label.toLowerCase() + '; see the label or calculator') };
    var b = e.rate.b, unit, conv = 1, tw = 0;
    if (c.basis === 'ton') {
      if (b !== 'ton' && b !== '30ton') return { status: 'unresolved', short: 'No per-ton rate', reason: 'Label rate is not per ton' };
      unit = 'ton';
    } else {
      unit = 'bu';
      if (b === 'ton' || b === '30ton') {
        tw = TW[e.name];
        if (!(tw > 0)) return { status: 'unresolved', short: 'No bushel weight', reason: 'No bushel conversion for ' + c.label.toLowerCase() };
        conv = tw / 2000;
      }
    }
    var bp = basisPackage(p);
    if (!bp) return { status: 'noPrice', short: 'No online price', reason: 'No priced package is currently offered online' };
    var pk = netOf(bp.v), price = bp.v.price / 100;
    var value = price / pk.n * useRate / divisor(b) * conv;
    var text = unit === 'bu' ? (value * 100).toFixed(2) + '¢/bu' : usd(value) + '/ton';
    var ruleText = rateOverride > 0 ? 'the rate chosen in the table' : (RULE_TEXT[e.ver.cr] || 'label rate');
    // A chosen level still says when it is not a label-printed amount (an arithmetic midpoint, or derived from a mix ratio).
    var pts = (Array.isArray(e.ver.o) && Array.isArray(e.ver.op)) ? e.ver.op : (e.ver.p || []);
    var chosenIsPrinted = !pts.length || pts.some(function (x) { return near(x, useRate); });
    // V7: the page no longer labels a middle level as a "midpoint" (provenance stays in the record: o/op/cr); a rate derived
    // from a label mix ratio is still named as derived.
    var chosenClause = e.ver.cr === 'derived' ? ', derived from the label mix ratio (not printed on the label)' : '';
    // cnh = the note for the higher of two separate printed levels (the default cn describes the lower one)
    var note = e.ver.cn || '';
    if (rateOverride > 0 && e.ver.cnh && e.ver.cr === 'lower_printed' && (e.ver.p || []).length === 2 && near(rateOverride, Math.max(e.ver.p[0], e.ver.p[1]))) note = e.ver.cnh;
    return {
      status: 'ok', value: value, unit: unit, text: text, key: e.key, name: e.name,
      rate: useRate, rateUnit: pk.u, rateBasis: b, rule: rateOverride > 0 ? 'chosen' : e.ver.cr, ruleText: ruleText, printed: e.ver.p || [], note: note,
      pkg: pkgName(bp.v), net: pk.n, netUnit: pk.u, price: price, fallback: bp.fallback, intended: bp.intended || '', tw: tw, variantId: bp.v.id,
      describe: pkgName(bp.v) + ': ' + usd(price) + ' for ' + num(pk.n) + ' ' + pk.u + (bp.fallback ? ' (fallback: ' + (bp.intended || 'the configured package') + ' is not currently offered)' : '') +
        '; rate ' + rateNum(useRate) + ' ' + pk.u + ' ' + per(b) + (rateOverride > 0 ? chosenClause : ', ' + ruleText) +
        (tw ? '; converted at ' + tw + ' lb per bushel' : '') + (note ? '. ' + note : '')
    };
  }

  // Calculator hand-off: product, commodity and — when the comparison rate is one of the calculator's levels — that level.
  function calcParams(p, c) {
    load();
    var e = entry(p, c);
    if (!e || (e.ver && e.ver.nocalc)) return null; // no calculator product for this dataset entry
    if (FD[e.name] && (e.rate.b === 'ton' || e.rate.b === '30ton')) return null; // rice form must be chosen in the calculator
    var params = { product: e.key, commodity: e.name };
    // V6: the calculator opens on the same level as the comparison default. For two separate printed levels (Diacon IGR
    // Plus) that is the lower printed level, so the hand-off never lands on the calculator's unprinted middle value.
    if (Array.isArray(e.rate.r) && e.ver && e.ver.c > 0) { var i = e.rate.r.indexOf(e.ver.c); if (i >= 0) params.level = i; }
    // the rate the calculator will open on (lets a card say when it differs from the card's estimate)
    params.rate = Array.isArray(e.rate.r) ? (params.level != null ? e.rate.r[params.level] : null) : e.rate.r;
    return params;
  }
  function calcHref(base, params) {
    if (!params) return base;
    var q = 'product=' + encodeURIComponent(params.product) + '&commodity=' + encodeURIComponent(params.commodity);
    if (params.level != null) q += '&level=' + params.level;
    return base + (base.indexOf('?') >= 0 ? '&' : '?') + q;
  }

  window.FSSEstimators = window.FSSEstimators || {};
  window.FSSEstimators.grain = { init: init, comparison: comparison, rateModel: rateModel, unitText: unitText, rateNum: rateNum, calcParams: calcParams, calcHref: calcHref, basisPackage: function (p) { load(); return basisPackage(p); } };
})();
